/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * MSRP price-visibility preference (CRCB2B-114, header half).
 *
 * ## PRESENTATION ONLY — NOT WIRED UP
 *
 * This exists so the header's MSRP toggle has somewhere to write. **No price
 * component reads it**, so flipping the toggle changes nothing a shopper sees.
 * The price-display half is CRCB2B-114.
 *
 * ## Read this before building on it
 *
 * The serialization below is hand-rolled and deliberately does NOT go through
 * `createCookie()` / `getCookieConfig()` in `lib/cookie-utils.server.ts`, which is
 * how every other storefront cookie is written. That is acceptable only while
 * nothing consumes the value. Three gaps to close before this drives real pricing:
 *
 * 1. **`Secure` is derived from the request protocol**, not `isRemote()`. On MRT,
 *    TLS terminates at the edge, so the origin request can be `http:` while the
 *    client is HTTPS — the flag can be dropped in production.
 * 2. **The name is not namespaced by site.** `getCookieNameWithSiteId()` writes
 *    `${name}_${siteId}` for everything except an explicit exclusion list, so on a
 *    multi-site storefront this preference is shared across sites.
 * 3. **No Page Designer design-mode handling** (`SameSite=None; Partitioned`), so
 *    the cookie is rejected inside the Business Manager iframe.
 *
 * Fixing (2) also breaks `readMsrpVisibleFromCookie()` below, which reads a bare
 * name and has no site context on the client — so the real fix is likely to read
 * the preference server-side and pass it down, rather than patching this in place.
 */

/** Cookie name holding the MSRP visibility preference. */
export const MSRP_VISIBLE_COOKIE = 'sram_msrp_visible';

/** One year — a display preference should outlive a browsing session. */
const MSRP_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Reads the preference from `document.cookie`. Client-only.
 *
 * Returns `false` when absent, unparseable, or called during SSR — hiding MSRP is
 * the safe default for a B2B storefront, where contract pricing is the primary
 * number. The cookie is intentionally not `HttpOnly` so this can read it.
 */
export function readMsrpVisibleFromCookie(): boolean {
    if (typeof document === 'undefined') return false;
    const cookies = document.cookie.split(';').map((c) => c.trim());
    const match = cookies.find((c) => c.startsWith(`${MSRP_VISIBLE_COOKIE}=`));
    return match?.slice(MSRP_VISIBLE_COOKIE.length + 1) === 'true';
}

/**
 * Server-side counterpart to {@link readMsrpVisibleFromCookie}, for loaders and actions.
 *
 * **Currently unused** — scaffolding for the CRCB2B-114 price components. It has
 * therefore never run and has no test. It also reads a bare cookie name, so it
 * will not match once the write side is namespaced by site (gap 2 above).
 */
export function readMsrpVisibleFromRequest(request: Request): boolean {
    const header = request.headers.get('Cookie');
    if (!header) return false;
    const cookies = header.split(';').map((c) => c.trim());
    const match = cookies.find((c) => c.startsWith(`${MSRP_VISIBLE_COOKIE}=`));
    return match?.slice(MSRP_VISIBLE_COOKIE.length + 1) === 'true';
}

/**
 * Builds the `Set-Cookie` value persisting the preference.
 *
 * @param visible - whether MSRP should be shown
 * @param options.secure - emit `Secure` (see gap 1 in the module note)
 * @param options.domain - optional cookie domain, per `app.cookies.domain`
 */
export function serializeMsrpVisibleCookie(
    visible: boolean,
    options: { secure?: boolean; domain?: string } = {}
): string {
    const parts = [
        `${MSRP_VISIBLE_COOKIE}=${visible ? 'true' : 'false'}`,
        'Path=/',
        `Max-Age=${MSRP_COOKIE_MAX_AGE}`,
        'SameSite=Lax',
    ];
    if (options.domain) parts.push(`Domain=${options.domain}`);
    if (options.secure) parts.push('Secure');
    return parts.join('; ');
}
